<template>
  <div class="time-series-card">
    <div class="chart-header">
      <h3>{{ title }}</h3>
      <div class="chart-legend">
        <span class="legend-item">
          <span class="legend-dot completed"></span> {{ $t('completed') }}
        </span>
        <span class="legend-item">
          <span class="legend-dot errored"></span> {{ $t('failed') }}
        </span>
        <span class="legend-item">
          <span class="legend-dot timeout"></span> {{ $t('timeout') }}
        </span>
      </div>
    </div>

    <div v-if="!timeSeriesData || timeSeriesData.length === 0" class="empty-state">
      <p>No data available</p>
    </div>

    <div v-else class="chart-container">
      <div class="y-axis">
        <div class="y-label">{{ maxExecutions }}</div>
        <div class="y-label">{{ Math.round(maxExecutions * 0.75) }}</div>
        <div class="y-label">{{ Math.round(maxExecutions * 0.5) }}</div>
        <div class="y-label">{{ Math.round(maxExecutions * 0.25) }}</div>
        <div class="y-label">0</div>
      </div>
      <div class="chart-scroll-wrapper">
      <div class="chart-area" :style="chartAreaStyle">
        <div class="chart-grid">
          <div class="grid-line" v-for="i in 5" :key="i"></div>
        </div>
        <div class="chart-bars">
          <div 
            v-for="(day, index) in timeSeriesData" 
            :key="index"
            class="bar-column"
            @mouseenter="showTooltip(day, $event)"
            @mousemove="moveTooltip($event)"
            @mouseleave="hideTooltip"
          >
            <div class="bar-wrapper">
              <!-- Separate bars side by side -->
              <div 
                v-if="day.completed > 0"
                class="bar completed"
                :style="{ height: getBarHeight(day.completed) + '%' }"
              ></div>
              <div 
                v-if="day.errored > 0"
                class="bar errored"
                :style="{ height: getBarHeight(day.errored) + '%' }"
              ></div>
              <div 
                v-if="day.timeout > 0"
                class="bar timeout"
                :style="{ height: getBarHeight(day.timeout) + '%' }"
              ></div>
            </div>
            <div class="bar-label" v-if="index % labelStep === 0">{{ formatDate(day.date) }}</div>
          </div>
        </div>

        <div v-if="tooltip.visible" class="chart-tooltip" :style="{ top: tooltip.y + 'px', left: tooltip.x + 'px' }">
          <div class="tooltip-date">{{ tooltip.date }}</div>
          <div class="tooltip-row" v-if="tooltip.completed > 0"><span class="tooltip-dot completed"></span>{{ $t('completed') }}: <strong>{{ tooltip.completed }}</strong></div>
          <div class="tooltip-row" v-if="tooltip.errored > 0"><span class="tooltip-dot errored"></span>{{ $t('failed') }}: <strong>{{ tooltip.errored }}</strong></div>
          <div class="tooltip-row" v-if="tooltip.timeout > 0"><span class="tooltip-dot timeout"></span>{{ $t('timeout') }}: <strong>{{ tooltip.timeout }}</strong></div>
          <div class="tooltip-row total">Total: <strong>{{ tooltip.completed + tooltip.errored + tooltip.timeout }}</strong></div>
        </div>
      </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'WorkflowTimeSeriesChart',
  props: {
    timeSeriesData: {
      type: Array,
      default: () => []
    },
    title: {
      type: String,
      default: 'Execution Trends'
    }
  },
  data() {
    return {
      tooltip: {
        visible: false,
        x: 0,
        y: 0,
        date: '',
        completed: 0,
        errored: 0,
        timeout: 0
      }
    };
  },
  computed: {
    labelStep() {
      var n = this.timeSeriesData ? this.timeSeriesData.length : 1;
      if (n > 60) return 14;
      if (n > 30) return 7;
      if (n > 14) return 3;
      return 1;
    },
    chartAreaStyle() {
      var count = this.timeSeriesData ? this.timeSeriesData.length : 0;
      var minPx = count * 28;
      return { minWidth: minPx + 'px', width: '100%' };
    },
    maxExecutions() {
      if (!this.timeSeriesData || this.timeSeriesData.length === 0) return 0;
      let max = 0;
      this.timeSeriesData.forEach(day => {
        const total = (day.completed || 0) + (day.errored || 0) + (day.timeout || 0);
        if (total > max) max = total;
      });
      return Math.max(max, 1);
    }
  },
  methods: {
    getBarHeight(count) {
      if (!this.maxExecutions || this.maxExecutions === 0) return 0;
      return (count / this.maxExecutions) * 100;
    },

    formatDate(dateStr) {
      const date = new Date(dateStr);
      const month = date.toLocaleDateString('en-US', { month: 'short' });
      const day = date.getDate();
      return `${month} ${day}`;
    },

    formatFullDate(dateStr) {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    },

    showTooltip(day, event) {
      this.tooltip.visible = true;
      this.tooltip.date = this.formatFullDate(day.date);
      this.tooltip.completed = day.completed || 0;
      this.tooltip.errored = day.errored || 0;
      this.tooltip.timeout = day.timeout || 0;
      this.moveTooltip(event);
    },

    moveTooltip(event) {
      var tooltipW = 180;
      var tooltipH = 120;
      var x = event.clientX + 10;
      var y = event.clientY - tooltipH - 8;
      // flip right→left if overflows viewport right
      if (x + tooltipW + 8 > window.innerWidth) {
        x = event.clientX - tooltipW - 10;
      }
      if (x < 4) x = 4;
      // flip below cursor if tooltip would go above viewport top
      if (y < 4) {
        y = event.clientY + 10;
      }
      this.tooltip.x = x;
      this.tooltip.y = y;
    },

    hideTooltip() {
      this.tooltip.visible = false;
    }
  }
};
</script>

<style scoped>
.time-series-card {
  background: var(--bg-card);
  border: 1px solid var(--border-default);
  border-radius: 8px;
  padding: 20px;
  position: relative;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.chart-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
}

.chart-legend {
  display: flex;
  gap: 16px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #9ca3af;
}

.legend-dot {
  width: 12px;
  height: 12px;
  border-radius: 2px;
}

.legend-dot.completed {
  background-color: #10b981;
}

.legend-dot.errored {
  background-color: #ef4444;
}

.legend-dot.timeout {
  background-color: #fb923c;
}

.loading-state,
.empty-state {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  color: #9ca3af;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--border-default);
  border-top-color: var(--primary-start);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.chart-container {
  display: flex;
  gap: 8px;
  padding: 20px 0;
  align-items: flex-start;
}

.y-axis {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 40px;
  height: 250px;
  font-size: 12px;
  color: #9ca3af;
  text-align: right;
  padding-right: 8px;
}

.y-label {
  line-height: 1;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.chart-scroll-wrapper {
  flex: 1;
  overflow-x: auto;
}

.chart-area {
  position: relative;
  height: 290px;
}

.chart-grid {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  height: 250px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  pointer-events: none;
}

.grid-line {
  border-bottom: 1px solid var(--border-default);
  width: 100%;
}

.chart-bars {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  height: 290px;
  display: flex;
  gap: 8px;
  padding: 0 4px;
}

.bar-column {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 20px;
  height: 100%;
}

.bar-wrapper {
  flex: 0 0 250px;
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: flex-end;
  gap: 2px;
}

.bar {
  transition: all 0.3s ease;
  border-radius: 4px 4px 0 0;
  min-height: 4px;
  flex: 0 0 auto;
  cursor: pointer;
  width: 12px;
}

.bar:hover {
  opacity: 0.8;
  transform: translateY(-2px);
}

.bar.completed {
  background-color: #10b981;
}

.bar.errored {
  background-color: #ef4444;
}

.bar.timeout {
  background-color: #fb923c;
}

.bar-label {
  font-size: 11px;
  color: #9ca3af;
  white-space: nowrap;
  text-align: center;
  margin-top: 6px;
  transform: rotate(-45deg);
  transform-origin: center;
}

.chart-tooltip {
  position: fixed;
  z-index: 9999;
  background: var(--bg-overlay, #1f2937);
  border: 1px solid var(--border-default);
  border-radius: 6px;
  padding: 10px 12px;
  pointer-events: none;
  min-width: 160px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.3);
}

.tooltip-date {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 6px;
  white-space: nowrap;
}

.tooltip-row {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #9ca3af;
  margin-top: 3px;
}

.tooltip-row.total {
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px solid var(--border-default);
  color: var(--text-primary);
}

.tooltip-dot {
  width: 8px;
  height: 8px;
  border-radius: 2px;
  flex-shrink: 0;
}

.tooltip-dot.completed { background-color: #10b981; }
.tooltip-dot.errored   { background-color: #ef4444; }
.tooltip-dot.timeout   { background-color: #fb923c; }
</style>
